import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { injectIntl, intlShape } from 'react-intl';
import { TextField, Tab, TabItem, Icon, Checkbox, Label } from 'mw-style-react';
import AppUtils from '@control-front-end/utils/utils';
import Calendar from './Calendar';
import FormOptionColor from '../../FormOptionColor';
import mes from '../intl';

import '../Panel.scss';

const _ITEMS_ = {
  check: Checkbox,
  calendar: Calendar,
  edit: TextField,
};

/**
 * Компонент Multi
 */
class Multi extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeTab: typeof props.value === 'string' ? 'variable' : 'static',
    };
    this.handleAnswersChange = this.handleAnswersChange.bind(this);
    this.handleAddAnswer = this.handleAddAnswer.bind(this);
    this.handleRemoveAnswer = this.handleRemoveAnswer.bind(this);
    this.handleExtraFieldChange = this.handleExtraFieldChange.bind(this);
  }

  shouldComponentUpdate(nextProps, nextState) {
    const { value } = this.props;
    const { activeTab } = this.state;
    return (
      JSON.stringify(nextProps.value) !== JSON.stringify(value) ||
      nextState.activeTab !== activeTab
    );
  }

  componentDidMount() {
    const { value } = this.props;
    // Добавить пустой ответ
    if (this.addAnswerEl && (!value || !value.length)) this.addAnswerEl.click();
  }

  // Ф-ция локализации
  i(mesId, values) {
    return AppUtils.getMes(this.context)(mesId, values);
  }

  // Добавление нового статического ответа
  handleAddAnswer() {
    const { id, onChange } = this.props;
    let { value } = this.props;
    value = typeof value === 'string' || !value ? [] : value.slice();
    value.push({ value: AppUtils.createRid(), title: '' });
    onChange({ id, value });
  }

  // Удаление статического ответ
  handleRemoveAnswer(index, optLen) {
    if (optLen <= 1) return;
    const { id, onChange } = this.props;
    const value = this.props.value.slice();
    value.splice(index, 1);
    onChange({ id, value });
  }

  // Ввод статического ответа
  handleAnswersChange({ index, value: optVal, title, color }) {
    const { id, onChange } = this.props;
    const value = this.props.value.slice();
    value.splice(index, 1, { value: optVal, title, color });
    onChange({ id, value });
  }

  // Отрисовать ответы
  renderAnswers(answers) {
    if (!answers) return null;
    answers = typeof answers === 'string' ? [] : answers;
    return answers.map(({ value, title, color }, index) => (
      <div key={index} styleName="panel__multi__content__item">
        <TextField
          id={`value_${value}`}
          label={this.i(mes.value)}
          value={value}
          bordered={true}
          onChange={({ value: newValue }) =>
            this.handleAnswersChange({ index, value: newValue, title, color })
          }
        />
        <TextField
          id={`title_${value}`}
          label={this.i(mes.title)}
          value={title}
          bordered={true}
          onChange={({ value: newTitle }) =>
            this.handleAnswersChange({ index, value, title: newTitle, color })
          }
        />
        <div styleName="panel__multi__content__item__color">
          <Label fontSize="small" value="Color:" />
          <FormOptionColor
            value={color}
            onChange={({ hex }) => {
              this.handleAnswersChange({ index, value, title, color: hex });
            }}
          />
        </div>
        <Icon
          type="trash"
          onClick={() => this.handleRemoveAnswer(index, answers.length)}
          visibility={answers.length > 1 ? 'visible' : 'disabled'}
        />
      </div>
    ));
  }

  // Отрисовать статические ответы
  renderStaticAnswers() {
    const { value } = this.props;
    return (
      <div>
        <div styleName="panel__multi__content__wrap">
          {this.renderAnswers(value)}
        </div>
        <div
          ref={(el) => {
            this.addAnswerEl = el;
          }}
          styleName="panel__multi__content__add"
          onClick={this.handleAddAnswer}
        >
          {this.i(mes.addAnswer)}
        </div>
      </div>
    );
  }

  // Ввод значений для extra полей
  handleExtraFieldChange({ className, id: fieldId, value: fieldValue }) {
    const { id, value, onChange } = this.props;
    const copyValue = { ...value };
    fieldValue = className === 'calendar' ? fieldValue.startDate : fieldValue;
    copyValue[fieldId] = fieldValue;
    onChange({ id, value: copyValue });
  }

  // Отрисовать поля extra
  renderExtraFields(value, extraFields) {
    const { errors } = this.props;
    value = typeof value === 'string' || !value ? {} : value;
    return extraFields.map(({ id, title, type, class: className }) => {
      const ItemName = _ITEMS_[className]; // NOSONAR
      let fieldValue = value[id] || '';
      fieldValue =
        className === 'calendar' ? { startDate: fieldValue } : fieldValue;
      const visibility =
        id === 'rows' && !value.multiline ? 'disabled' : 'visible';
      return (
        <div key={id} styleName="panel__multi__extra">
          <ItemName
            styleName={className}
            id={id}
            type={type}
            label={title}
            title={title}
            value={fieldValue}
            errors={errors}
            bordered={true}
            noVar={true}
            time={value.time}
            timeZone={value.timeZone}
            visibility={visibility}
            onChange={(e) => this.handleExtraFieldChange({ className, ...e })}
          >
            <Label value={title} />
          </ItemName>
        </div>
      );
    });
  }

  // Отрисовать статический блок для extra
  renderStaticExtra() {
    const { value, extraFields } = this.props;
    return <div>{this.renderExtraFields(value, extraFields)}</div>;
  }

  // Отрисовать статические значение для extra или options
  renderStatic() {
    const { extraFields } = this.props;
    return extraFields ? this.renderStaticExtra() : this.renderStaticAnswers();
  }

  // Отрисовать поле для ввода переменной
  renderVar() {
    const { id, value, errors, handleVarChange } = this.props;
    const error = errors[id];
    return (
      <TextField
        id={id}
        styleName="panel__multi__var"
        bordered={true}
        value={typeof value === 'string' ? value : ''}
        error={!!error}
        helperText={error || ''}
        placeholder="Enter variable in format {{VAR_NAME}}"
        onChange={handleVarChange}
        autoFocus={true}
      />
    );
  }

  render() {
    const { activeTab } = this.state;
    return (
      <div styleName="panel__multi">
        <div styleName="panel__multi__tabs">
          <Tab
            value={activeTab}
            onChange={({ value }) => this.setState({ activeTab: value })}
            underline={true}
          >
            <TabItem label={this.i(mes.static)} value="static" />
            <TabItem label={this.i(mes.variable)} value="variable" />
          </Tab>
        </div>
        <div styleName="panel__multi__content">
          {activeTab === 'static' ? this.renderStatic() : this.renderVar()}
        </div>
      </div>
    );
  }
}

Multi.defaultProps = {
  errors: {},
};

Multi.propTypes = {
  id: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
    PropTypes.string,
  ]),
  extraFields: PropTypes.array,
  errors: PropTypes.object,
  onChange: PropTypes.func,
  handleVarChange: PropTypes.func,
};

Multi.contextTypes = {
  intl: intlShape,
};

export default injectIntl(Multi);
